import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, CloudDownload, Radio, RotateCcw } from "lucide-react";
import { HEADER_QK, headerApi, type DataFreshness } from "../../api/headerApi";
import { useApiQuery } from "../../hooks/useApiQuery";
import { useLiveConnectionState } from "../../hooks/useLiveUpdates";
import { formatAgo, formatDateTime } from "../../lib/format";
import { chipAriaLabel, chipLook, freshnessPollMs, liveWords } from "./syncChipModel";

// Release 1.5.0 sync status chip (O-R53), ported from Summit Engineering's RefreshControl: the chip says how old the plant
// data is in words with an icon; its menu says "Data as of", what the colour means, the GSS connection, each source's own
// age, how this page keeps current (live-1-5), a "Refresh this page" that re-reads the page only (GSS is not contacted),
// and for an Admin "Sync from GSS now" (the existing signed request on Data sync — nothing new is written from the header).
// D-O221: the menu is drawn in a portal on the page body, anchored under the chip. The header clips its own overflow, so a
// menu drawn inside it never showed; and it always opens, whatever the sync is doing or whether the data has arrived.

const PANEL_WIDTH = 384;
const EDGE = 8;

export function SyncStatusChip({ fixture }: { fixture?: DataFreshness } = {}) {
  const live = useLiveConnectionState();
  const queryClient = useQueryClient();
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<CSSProperties>({ top: 56, right: EDGE, width: PANEL_WIDTH });
  const wrapRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const query = useApiQuery<DataFreshness>([...HEADER_QK.freshness], headerApi.freshness, {
    enabled: !fixture,
    staleTime: 15_000,
    refetchInterval: (q) => freshnessPollMs(Boolean(q.state.data?.running), live)
  });
  const data = fixture ?? query.data;
  const look = chipLook(data?.state);
  const Icon = look.icon;
  const label = data?.label ?? (query.isError ? "Data age unknown" : "Data age…");
  const liveText = liveWords(live);
  const spin = look.spin ? "animate-spin motion-reduce:animate-none" : "";

  const reposition = useCallback(() => {
    const rect = toggleRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.min(PANEL_WIDTH, Math.max(240, window.innerWidth - EDGE * 2));
    const right = Math.max(EDGE, window.innerWidth - rect.right);
    setPlace({ top: Math.round(rect.bottom + 8), right: Math.min(right, Math.max(EDGE, window.innerWidth - width - EDGE)), width });
  }, []);

  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open, reposition]);

  const gss = data?.gss ?? null;
  const asOf = data?.dataAsOfPlant ?? null;

  const panel = (
    <section
      ref={panelRef}
      id={panelId}
      aria-label="Plant data age and sources"
      style={place}
      className="fixed z-50 max-h-[80vh] space-y-3 overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 text-sm shadow-xl"
      data-testid="sync-chip-menu"
    >
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-testid="sync-chip-asof">
          {asOf ? `Data as of ${asOf}` : "Data as of — not yet read"}
        </h2>
        <p className="mt-0.5 flex items-center gap-1.5 text-base font-semibold text-slate-900">
          <Icon aria-hidden="true" className={`h-4 w-4 ${spin}`} />
          {label}
        </p>
        {data ? (
          <p className="mt-0.5 text-xs text-slate-600" data-testid="sync-chip-status-line">
            {data.words}
            {data.dataSource ? ` · source ${data.dataSource}` : ""}
            {data.lastSuccessUtc ? ` · last good read ${formatDateTime(data.lastSuccessUtc)}` : ""}
          </p>
        ) : (
          <p className="mt-0.5 text-xs text-slate-600">{query.isError ? "The data age could not be read just now." : "Reading the data age…"}</p>
        )}
        {data?.ageRule && (
          <p className="mt-1 text-[11px] leading-snug text-slate-500" data-testid="sync-chip-age-rule">
            {data.ageRule}
          </p>
        )}
        {query.isError && !fixture && data && <p className="mt-1 text-xs text-red-700">The data age could not be refreshed just now.</p>}
      </div>

      <p className="flex items-center gap-1.5 text-xs text-slate-600" data-testid="sync-chip-live" data-live={live.status}>
        <Radio aria-hidden="true" className="h-3.5 w-3.5" />
        {liveText ? (
          <>
            <span className="font-medium text-slate-800">Live updates on</span> · {liveText}
            {live.lastChangeAt ? ` · last change on this page ${formatAgo(new Date(live.lastChangeAt).toISOString())}` : ""}
          </>
        ) : (
          <>
            <span className="font-medium text-slate-800">Live updates off</span> · use “Refresh this page” to re-read
          </>
        )}
      </p>

      {gss && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" data-testid="sync-chip-gss">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">GSS connection</h3>
          <p className="mt-1 text-xs text-slate-700">{gss.importStatus}</p>
          <dl className="mt-1.5 space-y-0.5 text-[11px] text-slate-600">
            <div className="flex gap-1">
              <dt className="font-medium">Route:</dt>
              <dd>
                Fabric pipeline → staged import · gateway {gss.gatewayName} · connection {gss.connectionName}
              </dd>
            </div>
            <div className="flex gap-1">
              <dt className="font-medium">Refresh window:</dt>
              <dd data-testid="sync-chip-window">{gss.refreshWindow}</dd>
            </div>
          </dl>
        </div>
      )}

      {data && data.sources.length > 0 && (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="sync-chip-sources">
          {data.sources.map((s) => {
            const SIcon = chipLook(s.state).icon;
            return (
              <li key={s.id} className="flex items-start justify-between gap-3 px-3 py-2" data-source={s.id} data-state={s.state}>
                <div className="min-w-0">
                  <div className="text-xs font-medium text-slate-800">{s.label}</div>
                  {s.note && <div className="text-[11px] leading-snug text-slate-500">{s.note}</div>}
                </div>
                <div className="flex flex-none items-center gap-1 text-xs text-slate-700" title={s.words}>
                  <SIcon aria-hidden="true" className="h-3.5 w-3.5" />
                  <span>{s.status}</span>
                  <span className="sr-only"> ({s.words})</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
        {data?.canRunSync && (
          <Link
            to="/admin/data-sync#refresh-now"
            className="btn-primary inline-flex items-center gap-1.5 !px-2.5 text-xs"
            onClick={() => setOpen(false)}
            data-testid="sync-chip-run-sync"
          >
            <CloudDownload aria-hidden="true" className="h-3.5 w-3.5" />
            Sync from GSS now
          </Link>
        )}
        <button
          type="button"
          className="btn-secondary inline-flex items-center gap-1.5 !px-2.5 text-xs"
          onClick={() => {
            void queryClient.invalidateQueries({ refetchType: "active" });
            setOpen(false);
          }}
          data-testid="sync-chip-refresh"
        >
          <RotateCcw aria-hidden="true" className="h-3.5 w-3.5" />
          Refresh this page
        </button>
      </div>
      {data?.canRunSync && (
        <Link
          to="/admin/data-sync"
          className="block text-xs font-medium text-brand-700 underline decoration-brand-300 underline-offset-2 hover:text-brand-600"
          onClick={() => setOpen(false)}
          data-testid="sync-chip-open-data-sync"
        >
          Open GSS data sync — history, per-object results, rebuild
        </Link>
      )}
      <p className="text-[11px] leading-snug text-slate-500">
        “Refresh this page” re-reads this page from Summit Operations; GSS is not contacted.
        {data?.canRunSync
          ? " “Sync from GSS now” opens Data sync, where you give a reason (and sign outside the window)."
          : " Only an Admin can start a GSS sync; it runs on its own schedule inside the refresh window."}
      </p>
    </section>
  );

  return (
    <div ref={wrapRef} className="relative flex-none" data-testid="sync-chip-wrap">
      <button
        ref={toggleRef}
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={chipAriaLabel(label, data?.words)}
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full border px-1.5 text-xs font-medium md:px-2.5 ${look.className}`}
        data-testid="sync-chip"
        data-state={data?.state ?? "unknown"}
      >
        <Icon aria-hidden="true" className={`h-3.5 w-3.5 flex-none ${spin}`} strokeWidth={2.5} />
        <span className="hidden md:inline" data-testid="sync-chip-label">
          {label}
        </span>
        <ChevronDown aria-hidden="true" className={`hidden h-3 w-3 md:block ${open ? "rotate-180" : ""}`} />
      </button>

      {open && createPortal(panel, document.body)}
    </div>
  );
}
